import { Zap } from "lucide-react";
import { type QuizConfig } from "~/types/quiz";

export const testAdhd: QuizConfig = {
  id: "test-adhd",
  illustration: {
    src: "/images/quizzes/test-adhd.webp",
    alt: "Muškarcu pažnju sa započetog rada privlači ptica na prozoru.",
  },
  lastModified: "2026-07-18",
  title: "ADHD test: Prepoznaješ li znake kod sebe?",
  shortTitle: "ADHD test",
  description:
    "Besplatan ADHD test na srpskom. Proveri koliko su izraženi znaci nepažnje, hiperaktivnosti i impulsivnosti kod tebe.",
  longDescription:
    "Ovaj ADHD test pomaže ti da prepoznaš obrasce nepažnje, hiperaktivnosti i impulsivnosti u svakodnevnom životu. Kroz 12 pitanja zasnovanih na kliničkim kriterijumima za ADHD kod odraslih, otkrivamo koliko ovi obrasci utiču na tvoj posao, odnose i organizaciju. Na kraju dobijaš personalizovanu procenu, konkretne savete za upravljanje simptomima i preporuku da li bi bilo korisno da se konsultuješ sa stručnjakom.",
  icon: Zap,
  duration: "3-5 min",
  category: "Mentalno zdravlje",
  relatedDirectorySlugs: ["adhd-odrasli"],
  focusAreasWanted: [
    "adhd",
    "procrastination",
    "motivation",
    "self_development",
  ],
  featured: false,
  promptExtension: `
    ## ADHD-Specific Instructions
    - This quiz is based on ASRS (Adult ADHD Self-Report Scale) domains: inattention and hyperactivity-impulsivity.
    - NEVER suggest the user has ADHD. Frame results as "indicators" or "patterns consistent with ADHD traits."
    - Clearly state that only a clinical professional can diagnose ADHD.
    - For high scores (60+), strongly recommend professional consultation (psychiatrist or clinical psychologist).
    - Mention that ADHD in adults often looks different than in children: less hyperactivity, more disorganization and emotional dysregulation.
    - If the user scores high on inattention but low on hyperactivity, mention that inattentive-type ADHD is common in adults (especially women).
    - Include practical strategies: external structure tools (timers, reminders), breaking tasks into small chunks, body doubling, regular exercise.
  `,
  questions: [
    {
      type: "radio",
      question: "Pol:",
      options: ["Muški", "Ženski", "Ne želim da se izjasnim"],
      scores: [0, 0, 0],
    },
    {
      type: "radio",
      question:
        "Koliko često ti je teško da se koncentrišeš na detalje ili praviš greške iz nepažnje?",
      options: [
        "Nikad",
        "Retko",
        "Ponekad",
        "Često",
        "Veoma često",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question:
        "Koliko često ti je teško da održiš pažnju na zadatku ili aktivnosti koja te ne zanima previše?",
      options: [
        "Nikad",
        "Retko",
        "Ponekad",
        "Često",
        "Veoma često",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question:
        "Koliko često odlažeš ili izbegavaš zadatke koji zahtevaju duži mentalni napor?",
      options: [
        "Nikad",
        "Retko",
        "Ponekad",
        "Često",
        "Veoma često",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question:
        "Koliko često zaboravljaš dogovore, obaveze ili gde si ostavio/la stvari?",
      options: [
        "Nikad",
        "Retko",
        "Ponekad",
        "Često",
        "Veoma često",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question:
        "Koliko često ti je teško da organizuješ zadatke i upravljaš vremenom?",
      options: [
        "Nikad",
        "Retko",
        "Ponekad",
        "Često",
        "Veoma često",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question:
        "Koliko često osećaš unutrašnji nemir ili potrebu da stalno nešto radiš?",
      options: [
        "Nikad",
        "Retko",
        "Ponekad",
        "Često",
        "Veoma često",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question:
        "Koliko često prekidaš druge u razgovoru ili odgovaraš pre nego što sagovornik završi pitanje?",
      options: [
        "Nikad",
        "Retko",
        "Ponekad",
        "Često",
        "Veoma često",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question:
        "Koliko često donosiš impulzivne odluke bez razmišljanja o posledicama?",
      options: [
        "Nikad",
        "Retko",
        "Ponekad",
        "Često",
        "Veoma često",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "checkbox",
      checkboxScoring: "sum",
      question:
        "Koji od ovih problema primećuješ kod sebe? (označi sve što važi)",
      options: [
        "Teško mi je da pratim razgovore do kraja",
        "Često gubim fokus dok čitam ili gledam nešto",
        "Lako me ometaju zvuci ili dešavanja oko mene",
        "Često menjam aktivnosti, ne završavam započeto",
        "Teško mi je da sedim mirno na sastancima ili predavanjima",
        "Imam nagle promene raspoloženja",
        "Ništa od navedenog",
      ],
      scores: [1, 1, 1, 1, 1, 1, 0],
    },
    {
      type: "radio",
      question:
        "Koliko ovi obrasci utiču na tvoj posao, odnose ili svakodnevno funkcionisanje?",
      options: [
        "Ne utiču",
        "Malo utiču",
        "Umereno utiču",
        "Značajno utiču",
        "Veoma ozbiljno utiču",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "textarea",
      question:
        "Opiši kako ovi obrasci utiču na tvoj svakodnevni život (opciono):",
    },
  ],
  copy: {
    title: "Tvoj rezultat",
    subtitle: "Prepoznavanje ADHD obrazaca",
    scoreLabel: "Tvoj rezultat:",
    adviceTitle: "Saveti za tebe",
    resourcesTitle: "Korisni resursi",
    whatNextTitle: "Šta dalje?",
    shareButtonText: "Podeli rezultat",
  },
};
